<template>
  <div id="sec-feedback" class="px-3 pb-5">

    <!-- Skeleton -->
    <template v-if="isLoading">
      <div class="ch-section-header mb-3">
        <div class="fb-skel fb-skel-overline mb-2"></div>
        <div class="fb-skel fb-skel-title"></div>
      </div>
      <div class="fb-card">
        <div class="fb-skel-header">
          <div class="fb-skel fb-skel-icon"></div>
          <div style="flex:1">
            <div class="fb-skel fb-skel-line mb-1" style="width:55%"></div>
            <div class="fb-skel fb-skel-line" style="width:35%"></div>
          </div>
        </div>
        <div v-for="i in 3" :key="i" class="fb-skel-row">
          <div class="fb-skel fb-skel-num"></div>
          <div style="flex:1">
            <div class="fb-skel fb-skel-line mb-1" style="width:45%"></div>
            <div class="fb-skel fb-skel-stars"></div>
          </div>
        </div>
      </div>
    </template>

    <!-- CTA — feedback belum diisi -->
    <template v-else-if="!feedbackResult">
      <div class="ch-section-header mb-3">
        <p class="mb-1 overline">YOUR VOICE</p>
        <h2 class="heading-6-1 mb-0" style="letter-spacing:-0.3px">How are we doing?</h2>
      </div>
      <div class="bg-white rounded-6 box-shadow-bold p-3">
        <p class="color-grey mb-4 text-13 lh-1-5">
          Rate your experience so far — your feedback helps our team improve.
        </p>
        <button @click="$emit('navigate', 'ConciergeFeedback')" class="ch-btn-feedback w-100 py-3 rounded-6 font-bold text-15" type="button">
          Give Feedback
        </button>
      </div>
    </template>

    <!-- Result card — feedback sudah diisi -->
    <template v-else>
      <div class="ch-section-header mb-3">
        <p class="mb-1 overline">YOUR VOICE</p>
        <h2 class="heading-6-1 mb-0" style="letter-spacing:-0.3px">Your Feedback</h2>
      </div>
      <div class="fb-card">

        <!-- Header -->
        <div class="fb-result-header">
          <div class="fb-check-icon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1A8F44" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          </div>
          <div>
            <div class="fb-result-title">Feedback Submitted</div>
            <div class="fb-result-date">{{ submittedDate }}</div>
          </div>
        </div>

        <!-- Body: daftar pertanyaan & jawaban -->
        <div class="fb-result-body">
          <div v-for="(ans, idx) in answers" :key="idx" class="fb-q-block">
            <div class="fb-q-num">{{ idx + 1 }}</div>
            <div class="fb-q-content">
              <div class="fb-q-label">{{ ans.label }}</div>

              <!-- STAR -->
              <div v-if="ans.type === 'STAR'" class="fb-stars">
                <span
                  v-for="(star, si) in starArray(ans.value)"
                  :key="si"
                  :class="star.filled ? 'fb-star-full' : 'fb-star-empty'"
                >★</span>
              </div>

              <!-- TEXT -->
              <div v-else-if="ans.type === 'TEXT'" class="fb-ans-text">"{{ ans.value }}"</div>

              <!-- MULTIPLE_CHOICE -->
              <div v-else-if="ans.type === 'MULTIPLE_CHOICE'" class="fb-chips">
                <span
                  v-for="(v, ci) in (Array.isArray(ans.value) ? ans.value : [ans.value])"
                  :key="ci"
                  class="fb-chip"
                >{{ v }}</span>
              </div>

              <!-- CHOICE -->
              <span v-else class="fb-chip">{{ ans.value }}</span>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="fb-result-footer">
          <span class="fb-badge">&#10003; Submitted</span>
        </div>

      </div>
    </template>

  </div>
</template>

<script>
  import { formatDate } from '@/utils/index.js'

  export default {
    name: 'ConciergeFeedbackSection',

    props: {
      feedbackResult: { type: Object,  default: null  },
      isLoading:      { type: Boolean, default: false },
    },

    computed: {
      answers() {
        if (!this.feedbackResult || !this.feedbackResult.question_answer) return []
        return this.feedbackResult.question_answer
          .slice()
          .sort(function(a, b) {
            var posA = (a.feedback_form_question && a.feedback_form_question.position) || 0
            var posB = (b.feedback_form_question && b.feedback_form_question.position) || 0
            return posA - posB
          })
          .map(function(qa) {
            var q    = qa.feedback_form_question || {}
            var type = q.answer_type || ''
            var raw  = qa.text_answer
            var value = raw

            if (type === 'CHOICE') {
              if (raw && typeof raw === 'object') {
                value = raw.value_en || raw.value || ''
              } else if (typeof raw === 'string') {
                try {
                  var parsed = JSON.parse(raw)
                  value = (parsed && (parsed.value_en || parsed.value)) || raw
                } catch (e) { /* raw string, use as-is */ }
              }
            } else if (type === 'MULTIPLE_CHOICE') {
              var arr = Array.isArray(raw) ? raw : []
              if (!Array.isArray(raw) && typeof raw === 'string') {
                try { arr = JSON.parse(raw) } catch (e) { arr = [raw] }
              }
              value = arr.map(function(v) {
                if (v && typeof v === 'object') return v.value_en || v.value || ''
                return v
              })
            }

            return {
              label: q.question_en || '',
              type:  type,
              value: value,
            }
          })
      },

      submittedDate() {
        if (!this.feedbackResult || !this.feedbackResult.created_at) return ''
        return formatDate(this.feedbackResult.created_at)
      },
    },

    methods: {
      starArray(value) {
        var rounded = Math.round(Number(value) || 0)
        return [1, 2, 3, 4, 5].map(function(i) { return { filled: i <= rounded } })
      },
    },
  }
</script>

<style scoped>
.ch-section-header { padding-top: 8px; }

/* ── CTA button ────────────────────────────────── */
.ch-btn-feedback {
  background: #1C2B36;
  color: #fff;
  border: none;
  cursor: pointer;
  font-family: inherit;
  -webkit-tap-highlight-color: transparent;
  transition: filter 0.12s;
}
.ch-btn-feedback:active { filter: brightness(0.88); }

/* ── Result card shell ─────────────────────────── */
.fb-card {
  background: #fff;
  border-radius: 16px;
  border: 1px solid #E0E8EF;
  box-shadow: 0 1px 2px rgba(15,30,50,0.04), 0 4px 14px rgba(15,30,50,0.06);
  overflow: hidden;
}

/* ── Result header ─────────────────────────────── */
.fb-result-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-bottom: 1px solid #F0F4F7;
}
.fb-check-icon {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: #E6F5EC;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.fb-result-title {
  font-size: 14px;
  font-weight: 800;
  color: #1C2B36;
}
.fb-result-date {
  font-size: 11px;
  color: #6B7886;
  margin-top: 2px;
}

/* ── Question blocks ───────────────────────────── */
.fb-result-body { padding: 4px 16px; }

.fb-q-block {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 0;
  border-bottom: 1px solid #F5F7FA;
}
.fb-q-block:last-child { border-bottom: none; }

.fb-q-num {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #EEF3F7;
  font-size: 11px;
  font-weight: 800;
  color: #2B5F7A;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 2px;
}
.fb-q-content { flex: 1; min-width: 0; }
.fb-q-label {
  font-size: 11.5px;
  font-weight: 700;
  color: #6B7886;
  margin-bottom: 5px;
}

/* Stars */
.fb-stars { display: flex; gap: 3px; }
.fb-star-full  { font-size: 18px; color: #C97B5C; }
.fb-star-empty { font-size: 18px; color: #E0E8EF; }

/* Text answer */
.fb-ans-text {
  font-size: 12.5px;
  font-style: italic;
  color: #4A5568;
  line-height: 1.5;
}

/* Chips */
.fb-chips { display: flex; flex-wrap: wrap; gap: 6px; }
.fb-chip {
  display: inline-block;
  font-size: 12px;
  font-weight: 700;
  background: #EEF3F7;
  color: #2B5F7A;
  padding: 4px 10px;
  border-radius: 99px;
}

/* ── Footer ────────────────────────────────────── */
.fb-result-footer {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border-top: 1px solid #F0F4F7;
}
.fb-badge {
  font-size: 11px;
  font-weight: 700;
  background: #E6F5EC;
  color: #1A8F44;
  padding: 4px 10px;
  border-radius: 99px;
}

/* ── Skeleton ──────────────────────────────────── */
.fb-skel {
  background: #E8EDF2;
  border-radius: 6px;
  animation: fbSkeletonPulse 1.5s ease-in-out infinite;
}
@keyframes fbSkeletonPulse {
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.5; }
}
.fb-skel-overline { height: 10px; width: 60px; }
.fb-skel-title    { height: 22px; width: 45%; }
.fb-skel-icon     { width: 40px; height: 40px; border-radius: 12px; flex-shrink: 0; }
.fb-skel-num      { width: 22px; height: 22px; border-radius: 50%; flex-shrink: 0; }
.fb-skel-line     { height: 12px; }
.fb-skel-stars    { height: 18px; width: 100px; border-radius: 4px; }

.fb-skel-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-bottom: 1px solid #F0F4F7;
}
.fb-skel-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 16px;
  border-bottom: 1px solid #F5F7FA;
}
.fb-skel-row:last-child { border-bottom: none; }

:deep(.text-13) { font-size: 13px !important; }
:deep(.text-15) { font-size: 15px !important; }
</style>



// WEBPACK FOOTER //
// src/components/v2/ConciergeFeedbackSection.vue